import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Play, Wifi, Calendar, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { 
  MobileHeader, MobileBottomNav, DesktopHeader 
} from '@/components/layout/MobileLayout';
import { mockEvents } from '@/lib/mockData';
import { formatDate, formatTime } from '@/lib/utils';

export function UserLivestreamJoin() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const event = mockEvents.find(e => e.id === eventId) || mockEvents[0];

  const handleJoin = () => {
    navigate(`/events/${eventId}/watch`);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="user-livestream-join">
      {/* Mobile Header */}
      <MobileHeader />
      
      {/* Desktop Header */}
      <DesktopHeader />
      
      <main className="pt-14 lg:pt-20 pb-24 lg:pb-12">
        <div className="container mx-auto px-4 max-w-2xl">
          <Card className="bg-card border-border overflow-hidden">
            {/* Event Image */}
            <div className="relative h-64">
              <img 
                src={event.coverImage || event.image} 
                alt={event.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
              <div className="absolute top-4 left-4">
                <span className="px-3 py-1 bg-red-600 text-white text-sm font-semibold rounded-full flex items-center gap-1">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  LIVE NOW
                </span>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Event Info */}
              <div className="text-center">
                <h1 className="text-2xl font-bold text-white mb-2">{event.title}</h1>
                <p className="text-muted-foreground">You're about to join the livestream</p>
              </div>

              {/* Event Details */}
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Calendar className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-white font-medium">{formatDate(event.date)}</p>
                    <p className="text-sm">{formatTime(event.date)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <MapPin className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-white font-medium">{event.venue}</p>
                    <p className="text-sm">{event.city}</p>
                  </div>
                </div>
              </div>

              {/* Connection Check */}
              <Card className="bg-secondary/50 border-border p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Wifi className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Connection Ready</p>
                    <p className="text-sm text-muted-foreground">Your internet connection is good for streaming</p>
                  </div>
                </div>
              </Card>

              {/* Viewers count */}
              <div className="text-center text-muted-foreground">
                <p><span className="text-primary font-semibold">2,547</span> people are currently watching</p>
              </div>

              {/* Join Button */}
              <Button 
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 h-14 text-lg font-semibold"
                onClick={handleJoin}
              >
                <Play className="w-5 h-5 mr-2" fill="currentColor" />
                JOIN LIVESTREAM
              </Button>

              <p className="text-center text-xs text-muted-foreground">
                By joining, you agree to our streaming terms and community guidelines
              </p>
            </div>
          </Card>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
}

export default UserLivestreamJoin;
